import { cn } from '../../lib/utils';

export interface CragSectorTab {
  id: string;
  name: string;
  /**
   * How many routes the tab holds, shown after its name. Absent on the tabs
   * that are not a sector — the info tab counts nothing.
   */
  climbsCount?: number;
}

interface CragSectorTabsProps {
  sectors: CragSectorTab[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  className?: string;
}

/**
 * The crag's sectors, as the tabs the topo below is read through.
 *
 * Same look as the gym's boulders/routes tabs, but scrolling rather than
 * sharing the row equally: a gym has two or three sections, a crag can have a
 * dozen cliffs and squeezing them into one row would leave every name cut to
 * three letters.
 */
export function CragSectorTabs({ sectors, selectedId, onSelect, className }: CragSectorTabsProps) {
  if (sectors.length === 0) return null;

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={cn('flex gap-1 overflow-x-auto rounded-lg bg-muted p-1', className)}
    >
      {sectors.map((sector) => {
        const isSelected = sector.id === selectedId;
        return (
          <button
            key={sector.id}
            role="tab"
            aria-selected={isSelected}
            onClick={() => onSelect(sector.id)}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-md px-3 py-1 text-sm transition-colors',
              isSelected
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {sector.climbsCount == null ? sector.name : `${sector.name} (${sector.climbsCount})`}
          </button>
        );
      })}
    </div>
  );
}
